import { ObjectId } from 'bson';
import { action, computed, makeObservable, runInAction } from 'mobx';
import type { Moment } from 'moment';

import { concatPath, logError, moment } from '@feathr/hooks';
import type { IBaseAttributes, TConstraints } from '@feathr/rachis';
import { Collection, DisplayModel, isWretchError, wretch } from '@feathr/rachis';

export type TSegmentExportState =
  | 'pending'
  | 'exporting'
  | 'ready'
  | 'failed'
  | 'stopped'
  | 'expired';

export type TSegmentExportType = 'email_activity' | 'persons' | 'breadcrumb';

export type TSegmentExportPhase = 'enumerating' | 'exporting';

/*
 * In-progress states — the only states a Stop action applies to. Mirrors
 * anhinga's `ACTIVE_EXPORT_STATES` frozenset on the SegmentExport model.
 */
const ACTIVE_STATES = new Set<TSegmentExportState>(['pending', 'exporting']);

export interface ISegmentExport extends IBaseAttributes {
  person_segment: string;
  account_id: string;
  export_type: TSegmentExportType;
  state: TSegmentExportState;
  phase: TSegmentExportPhase | null;
  params: Record<string, unknown>;
  email: string;
  name?: string;
  created_by: string;
  is_archived: boolean;
  date_created: string | null;
  date_started: string | null;
  date_finished: string | null;
  rows_written: number;
  files_written: number;
  file_size: number | null;
  current_chunk_index: number;
  total_chunks: number | null;
  /** Percent complete, 0-100, as reported by the BE. Null until the export starts. */
  progress: number | null;
  error: string | null;
}

export class SegmentExport extends DisplayModel<ISegmentExport> {
  public readonly className = 'SegmentExport';

  public get constraints(): TConstraints<ISegmentExport> {
    return {
      email: {
        presence: { allowEmpty: false },
      },
      person_segment: {
        presence: { allowEmpty: false },
      },
      export_type: {
        presence: { allowEmpty: false },
        inclusion: ['email_activity', 'persons', 'breadcrumb'],
      },
    };
  }

  constructor(attributes: Partial<ISegmentExport> = {}) {
    super(attributes);

    makeObservable(this);
  }

  public override getDefaults(): Partial<ISegmentExport> {
    return {
      is_archived: false,
      rows_written: 0,
      files_written: 0,
      current_chunk_index: 0,
    };
  }

  public getItemUrl(pathSuffix?: string): string {
    return concatPath(`/data/exports/${this.id}`, pathSuffix);
  }

  @computed
  public get name(): string {
    return this.get('name') ?? '';
  }

  @computed
  public get dateCreated(): Moment {
    const dateCreated = this.get('date_created');
    if (dateCreated) {
      return moment.utc(dateCreated);
    }
    return moment.utc(new ObjectId(this.id).getTimestamp());
  }

  @computed
  public get dateFinished(): Moment | null {
    const dateFinished = this.get('date_finished');
    return dateFinished ? moment.utc(dateFinished) : null;
  }

  @computed
  public get downloadUrl(): string {
    if (this.collection) {
      return `${this.collection.url()}${this.id}/download`;
    }
    return '';
  }

  /** Whether a Stop action applies — true only while the export is in progress. */
  @computed
  public get canStop(): boolean {
    return ACTIVE_STATES.has(this.get('state'));
  }

  /** Whether the export has finished writing files and can be downloaded. */
  @computed
  public get canDownload(): boolean {
    return this.get('state') === 'ready';
  }

  /**
   * Whether the export can be archived. `is_archived` is delete-equivalent and
   * orthogonal to `state`, so any not-yet-archived export is archivable
   * regardless of where it sits in the state machine.
   */
  @computed
  public get canArchive(): boolean {
    return !this.get('is_archived');
  }

  /**
   * Whether a previously-ready export's download link has aged out. BE-emitted
   * (`state` flips to `expired`); the UI shows a disabled Download with an
   * explanatory tooltip.
   */
  @computed
  public get isExpired(): boolean {
    return this.get('state') === 'expired';
  }
}

export class SegmentExports extends Collection<SegmentExport> {
  public getClassName(): string {
    return 'segment-exports';
  }

  public getModel(attributes: Partial<ISegmentExport>): SegmentExport {
    return new SegmentExport(attributes);
  }

  /**
   * Re-fetches every cached list URL silently and merges the response into the
   * existing model instances via `processListJSONResponse`.
   *
   * Unlike `refreshApiCache()` / `list({ reset: true })`, this does NOT clear
   * `models` or flip `isPending=true` on the cache entry, so consumers reading
   * those observables (e.g. the dashboard `Table`) won't render a "loading"
   * state during the refetch. Individual cells re-render only when the model
   * attributes they observe actually change. Designed for short-interval
   * polling (e.g. dashboard progress).
   *
   * All cache updates are applied in a single `runInAction` after every fetch
   * has resolved so consumers reading multiple cache entries (e.g. a page
   * computing `hasExports` from one URL while the `Table` inside it reads
   * another paginated/sorted URL) see a consistent snapshot in one render —
   * avoids a transient state where one cache entry has refreshed and another
   * hasn't.
   *
   * Refetches every cached list URL on each call (one request per
   * `Object.keys(this.apiCache)` entry), not just the currently-visible one.
   * For a single-page dashboard this is intended; a consumer with many cached
   * filter/page combos may want a more targeted variant.
   *
   * Fetch failures are swallowed (the row keeps its last-known values and the
   * next poll tick retries) but reported to Sentry via `logError` so consistent
   * failures stay visible.
   */
  @action
  public async refreshActiveListings(): Promise<void> {
    const urls = Object.keys(this.apiCache);
    const responses = await Promise.all(
      urls.map(async (url) => {
        const cached = this.apiCache[url];
        if (!cached || cached.isPending) {
          return null;
        }
        const response = await wretch<ISegmentExport>(url, {
          method: 'GET',
          headers: this.getHeaders(),
          ...cached.fetchOptions,
        });
        if (isWretchError(response)) {
          logError({
            message: 'Silent refresh of segment exports listing failed',
            level: 'warning',
            extra: { url, error: response.error.message },
          });
          return null;
        }
        return { url, response };
      }),
    );
    runInAction(() => {
      responses.forEach((entry) => {
        if (!entry) {
          return;
        }
        const { url, response } = entry;
        const cached = this.apiCache[url];
        if (!cached) {
          return;
        }
        const processed = this.processListJSONResponse(response, false);
        cached.models.replace(processed.models);
        if (processed.pagination) {
          Object.assign(cached.pagination, processed.pagination);
        }
      });
    });
  }
}
